import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Dashboard from "@/pages/Dashboard";
import ComingSoon from "@/pages/ComingSoon";
import FuelAccess from "@/pages/FuelAccess";
import FuelAccessDetails from "@/pages/FuelAccessDetails";
import FuelAccessView from "@/pages/FuelAccessView";
import Roles from "@/pages/Roles";
import Members from "@/pages/Members";
import ScanMessages from "@/pages/ScanMessages";
import ScanHistory from "@/pages/ScanHistory";
import OldVehicles from "@/pages/OldVehicles";
import VehicleRegistrationReport from "@/pages/reports/VehicleRegistrationReport";
import ScanReport from "@/pages/reports/ScanReport";
import VehiclesByUser from "@/pages/reports/VehiclesByUser";
import VehiclesByBunk from "@/pages/reports/VehiclesByBunk";
import VehiclesByCompany from "@/pages/reports/VehiclesByCompany";
import VehiclesByCity from "@/pages/reports/VehiclesByCity";
import VehiclesByDistrict from "@/pages/reports/VehiclesByDistrict";
import VehiclesByState from "@/pages/reports/VehiclesByState";
import VehiclesByCountry from "@/pages/reports/VehiclesByCountry";
import ScanByBunk from "@/pages/reports/ScanByBunk";
import ScanByUser from "@/pages/reports/ScanByUser";
import ScanByCompany from "@/pages/reports/ScanByCompany";
import ScanByCity from "@/pages/reports/ScanByCity";
import ScanByDistrict from "@/pages/reports/ScanByDistrict";
import ScanByState from "@/pages/reports/ScanByState";
import ScanByCountry from "@/pages/reports/ScanByCountry";
import GeoCrudPage from "@/components/master/GeoCrudPage";
import SimpleCrudPage from "@/components/master/SimpleCrudPage";
import { GEO_CONFIGS, FLAT_CONFIGS, HYBRID_CONFIGS } from "@/lib/masterConfig";
import { MASTER_DATA_ITEMS } from "@/components/Sidebar";

const SIMPLE_PAGES = [];

// Routes that now have a real CRUD page (by sidebar path)
const GEO_ROUTES = {
  "/master-data/countries": GEO_CONFIGS.country,
  "/master-data/states": GEO_CONFIGS.state,
  "/master-data/districts": GEO_CONFIGS.district,
  "/master-data/cities": GEO_CONFIGS.city,
  "/master-data/taluks": GEO_CONFIGS.taluk,
  "/master-data/areas": GEO_CONFIGS.area,
  "/master-data/streets": GEO_CONFIGS.street,
  "/master-data/pincodes": GEO_CONFIGS.pincode,
  "/master-data/retail-outlets": HYBRID_CONFIGS.retailOutlet,
  "/master-data/testing-agencies": HYBRID_CONFIGS.testingAgency,
};

const FLAT_ROUTES = {
  "/master-data/companies": FLAT_CONFIGS.company,
  "/master-data/wheel-counts": FLAT_CONFIGS.wheelCount,
  "/master-data/vehicle-brands": FLAT_CONFIGS.vehicleBrand,
  "/master-data/engine-stroke-types": FLAT_CONFIGS.engineStrokeType,
  "/master-data/cylinder-categories": FLAT_CONFIGS.cylinderCategory,
  "/master-data/fuel-access-prefixes": FLAT_CONFIGS.fuelAccessPrefix,
  "/master-data/cylinder-capacities": FLAT_CONFIGS.cylinderCapacity,
  "/master-data/test-years": FLAT_CONFIGS.testYear,
};

function protect(element) {
  return (
    <ProtectedRoute>
      <AppLayout>{element}</AppLayout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin/login" element={<Login />} />
            <Route path="/login" element={<Navigate to="/admin/login" replace />} />

            <Route path="/dashboard" element={protect(<Dashboard />)} />

            <Route path="/roles-permissions" element={protect(<Roles />)} />
            <Route path="/members" element={protect(<Members />)} />
            <Route
              path="/master-data/scan-messages"
              element={protect(<ScanMessages />)}
            />

            <Route path="/fuel-access" element={protect(<FuelAccess />)} />
            <Route
              path="/vehicle-registration"
              element={protect(
                <FuelAccess
                  statusFilter="blank"
                  pageTitle="Vehicle Registration"
                  pageSubtitle="Search by QR code to find and manage vehicle registration records."
                />
              )}
            />
            <Route
              path="/blank-fuel-access"
              element={<Navigate to="/vehicle-registration" replace />}
            />
            <Route path="/scan-history" element={protect(<ScanHistory />)} />
            <Route path="/old-vehicles" element={protect(<OldVehicles />)} />
            <Route
              path="/reports/vehicle-registration"
              element={protect(<VehicleRegistrationReport />)}
            />
            <Route
              path="/reports/scan"
              element={protect(<ScanReport />)}
            />
            <Route
              path="/reports/vehicles-by-user"
              element={protect(<VehiclesByUser />)}
            />
            <Route
              path="/reports/vehicles-by-bunk"
              element={protect(<VehiclesByBunk />)}
            />
            <Route
              path="/reports/vehicles-by-company"
              element={protect(<VehiclesByCompany />)}
            />
            <Route
              path="/reports/vehicles-by-city"
              element={protect(<VehiclesByCity />)}
            />
            <Route
              path="/reports/vehicles-by-district"
              element={protect(<VehiclesByDistrict />)}
            />
            <Route
              path="/reports/vehicles-by-state"
              element={protect(<VehiclesByState />)}
            />
            <Route
              path="/reports/vehicles-by-country"
              element={protect(<VehiclesByCountry />)}
            />
            <Route
              path="/reports/scan-by-user"
              element={protect(<ScanByUser />)}
            />
            <Route
              path="/reports/scan-by-bunk"
              element={protect(<ScanByBunk />)}
            />
            <Route
              path="/reports/scan-by-company"
              element={protect(<ScanByCompany />)}
            />
            <Route
              path="/reports/scan-by-city"
              element={protect(<ScanByCity />)}
            />
            <Route
              path="/reports/scan-by-district"
              element={protect(<ScanByDistrict />)}
            />
            <Route
              path="/reports/scan-by-state"
              element={protect(<ScanByState />)}
            />
            <Route
              path="/reports/scan-by-country"
              element={protect(<ScanByCountry />)}
            />


            <Route
              path="/fuel-access/:id"
              element={protect(<FuelAccessView />)}
            />
            <Route
              path="/fuel-access/:id/details"
              element={protect(<FuelAccessDetails />)}
            />

            {SIMPLE_PAGES.map(({ path, title }) => (
              <Route
                key={path}
                path={path}
                element={protect(
                  <ComingSoon
                    title={title}
                    description={`The ${title} module is under construction.`}
                  />
                )}
              />
            ))}

            {MASTER_DATA_ITEMS.map(({ path, label }) => {
              const geoConfig = GEO_ROUTES[path];
              const flatConfig = FLAT_ROUTES[path];
              let element;
              if (geoConfig) {
                element = <GeoCrudPage config={geoConfig} />;
              } else if (flatConfig) {
                element = <SimpleCrudPage config={flatConfig} />;
              } else {
                element = (
                  <ComingSoon
                    title={label}
                    description={`The ${label} master data module is under construction.`}
                  />
                );
              }
              return (
                <Route key={path} path={path} element={protect(element)} />
              );
            })}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
